import AxiosInstance, { takeLatestRequest } from "@/config/AxiosInstance";
import { API_BASE_URL } from "@/config/appConfig";

type RequestPayload = Record<string, unknown>;
type QueryPayload = Record<string, unknown> & {
  download?: boolean;
};
type BrandId = string | number;
type LatestRequestConfig = {
  headers?: Record<string, string>;
  method: string;
  params?: QueryPayload;
  responseType?: string;
  url: string;
};

export const subscriptionService = {
  getPackages,
  selfSubscribe,
  checkAndFetchParticipantLocations,
  getPackagesNew,
  getUserUnsubscribedLocations,
  getUserSubscribedLocations,
  saveSubscriptionSelection,
  clickHerePackageEmail,
  getUserLocationsSubscriptionDetail,
  getOptedOutData,
  getSubscriptionPage,
  optOutAutoRenewal,
  optInAutoRenewal,
  changePackageDetails,
};

function getPackages(brandId: BrandId) {
  return AxiosInstance.get(`${API_BASE_URL}/subscription-packages/${brandId}`);
}

function getPackagesNew(brandId: BrandId) {
  return AxiosInstance.get(
    `${API_BASE_URL}/subscriptionPackages?brandId=${brandId}`
  );
}

function selfSubscribe(data: RequestPayload) {
  return AxiosInstance.post(`${API_BASE_URL}/self-subscribe`, data);
}

function checkAndFetchParticipantLocations({
  brandId,
  email,
  packageId,
}: {
  brandId: BrandId;
  email: string;
  packageId: BrandId;
}) {
  const config: LatestRequestConfig = {
    method: "get",
    url: `${API_BASE_URL}/brand/${brandId}/isEmailExists/${email}`,
  };
  return takeLatestRequest(
    config,
    `checkAndFetchParticipantLocations${packageId}`
  );
}

function getUserUnsubscribedLocations(reqData: QueryPayload) {
  const config: LatestRequestConfig = {
    method: "get",
    url: `${API_BASE_URL}/unsubscribed-location-listing`,
    params: reqData,
    headers: { "Cache-Control": "no-cache", Pragma: "no-cache", Expires: "0" },
  };
  return takeLatestRequest(config, `unsubscribedLocationListing`);
}

function getUserSubscribedLocations(reqData: QueryPayload) {
  const config: LatestRequestConfig = {
    method: "get",
    url: `${API_BASE_URL}/subscribed-location-listing`,
    params: reqData,
    headers: { "Cache-Control": "no-cache", Pragma: "no-cache", Expires: "0" },
  };
  return takeLatestRequest(config, `subscribedLocationListing`);
}

function saveSubscriptionSelection(data: RequestPayload) {
  return AxiosInstance.post(
    `${API_BASE_URL}/save-subscription-selection`,
    data
  );
}

function clickHerePackageEmail(data: RequestPayload) {
  return AxiosInstance.post(`${API_BASE_URL}/click-here-package-email`, data);
}

function getUserLocationsSubscriptionDetail(
  reqData: QueryPayload = {},
  isSWR = false
) {
  const config: LatestRequestConfig = {
    method: "get",
    url: `${API_BASE_URL}/subscriptions-list`,
    params: reqData,
  };
  if (isSWR) {
    return takeLatestRequest(config, "userLocationsSubscriptions").then(
      (r) => r.data
    );
  }
  if (reqData.download) config.responseType = "arraybuffer";
  return takeLatestRequest(config, "userLocationsSubscriptions");
}

function getOptedOutData(reqData: QueryPayload = {}, isSWR = false) {
  const config: LatestRequestConfig = {
    method: "get",
    url: `${API_BASE_URL}/optout-list`,
    params: reqData,
  };
  if (isSWR) {
    return takeLatestRequest(config, "userLocationsSubscriptions").then(
      (r) => r.data
    );
  }
  if (reqData.download) config.responseType = "arraybuffer";
  return takeLatestRequest(config, "subscriptionOptedOut");
}

function getSubscriptionPage(brandId: BrandId) {
  return AxiosInstance.get(`${API_BASE_URL}/subscriptionPages/${brandId}`);
}

function optOutAutoRenewal(data: RequestPayload) {
  return AxiosInstance.post(
    `${API_BASE_URL}/subscription/locationOptOut`,
    data
  );
}

function changePackageDetails(data: RequestPayload) {
  return AxiosInstance.post(
    `${API_BASE_URL}/subscription/changePackageDetails`,
    data
  );
}

function optInAutoRenewal(data: RequestPayload) {
  return AxiosInstance.post(`${API_BASE_URL}/subscription/locationOptIn`, data);
}
